/**
 * V2AdminOSPage — Kindra OS internal review cockpit.
 *
 * Route: /admin/os                              (queue / landing)
 *        /admin/os/sessions/:sessionId           (drill-in detail + editor)
 *
 * Auth: reuses the existing /admin JWT in localStorage (`kindra-admin-token`)
 * — same login, same allowlist, no separate auth surface.
 *
 * Strict client firewall: this whole page is gated behind admin auth. NO
 * client-facing route renders any of this data. If the token is missing
 * or invalid the page redirects to /admin/login.
 *
 * Visual: V2 dark cockpit — navy backdrop, spectrum accents, leak-area
 * colours pulled from the existing /kindra/utils LEAK_META palette so the
 * cockpit feels like the same family as the marketing site.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import V2Logo from './V2Logo';
import V2FirstRunTour from './V2FirstRunTour';
import { SessionKeeper } from './SessionKeeper';
import { V2GroundedReport, PrintPdfButton } from './V2AdminReportPage';
import { Lively, CountUp } from './motion';
import { KioskControls, KioskExitPill, KioskGlobalStyles } from './V2KioskControls';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'kindra-admin-token';
const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};
const auth = () => ({ headers: { Authorization: `Bearer ${tokenStore.get()}` } });

// ─── Cockpit palette (TravelTrucks Aero dark) — shared by all OS pages ─────
export const C = {
  navy:    '#0E0F11',   // deep charcoal canvas (name kept for compatibility)
  surface: '#17181B',   // raised tile, slight lift over canvas
  hair:    'rgba(255,255,255,0.10)',
  hairBright: 'rgba(255,255,255,0.20)',
  icy:     '#F1F2F3',   // near-white ink on dark glass
  muted:   'rgba(241,242,243,0.64)',
  faint:   'rgba(241,242,243,0.42)',
  spectrum: 'linear-gradient(135deg, #F79640 0%, #EC7C24 55%, #C85A12 100%)',
};

// Leak-area → colour (backend uses snake_case, marketing site uses camelCase
// — map both, keep the cockpit visually consistent with the site).
const LEAK_COLOR = {
  time:        '#EC7C24',
  money:       '#C85A12',
  lost_work:   '#F79640',
  pricing:     '#FFB469',
  blind_spots: '#EC7C24',
};
const LEAK_LABEL = {
  time:        'Time',
  money:       'Money',
  lost_work:   'Lost Work',
  pricing:     'Pricing',
  blind_spots: 'Blind Spots',
};
const LEAK_ORDER = ['time', 'money', 'lost_work', 'pricing', 'blind_spots'];

// Severity styling for the report cards.
const SEV = {
  high:   { label: 'High',   bg: 'rgba(236,124,36,0.16)', border: 'rgba(236,124,36,0.45)', text: '#FFB469' },
  medium: { label: 'Medium', bg: 'rgba(200,90,18,0.16)', border: 'rgba(200,90,18,0.45)', text: '#FFB469' },
  low:    { label: 'Low',    bg: 'rgba(84,209,151,0.12)', border: 'rgba(84,209,151,0.40)', text: '#7FE0C0' },
  none:   { label: 'No signal', bg: 'rgba(241,242,243,0.06)', border: 'rgba(241,242,243,0.16)', text: 'rgba(241,242,243,0.55)' },
};

// ─── Tiny atoms ────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const today = new Date();
    const same = d.toDateString() === today.toDateString();
    const opts = same
      ? { hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    return d.toLocaleString('en-AU', opts);
  } catch { return iso; }
};

const StatusPill = ({ status, hasReport, reportError }) => {
  // "complete" = discovery ended, report background task in flight.
  // Show a subtle pulsing "Generating…" so the queue never looks dead.
  if (reportError) {
    return (
      <span
        data-testid="os-status-pill-report_failed"
        title={reportError}
        style={{
          display: 'inline-block',
          padding: '4px 10px', borderRadius: 999,
          fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
          fontFamily: 'Source Sans 3, system-ui, sans-serif',
          background: 'rgba(255,180,105,0.22)', color: '#FFB469', whiteSpace: 'nowrap',
        }}
      >⚠ Report failed</span>
    );
  }
  if (status === 'complete' && !hasReport) {
    return (
      <span
        data-testid="os-status-pill-generating"
        style={{
          display: 'inline-block',
          padding: '4px 10px', borderRadius: 999,
          fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
          fontFamily: 'Source Sans 3, system-ui, sans-serif',
          background: 'rgba(247,150,64,0.22)', color: '#F79640', whiteSpace: 'nowrap',
          animation: 'kindra-pulse 1.6s ease-in-out infinite',
        }}
      >
        <span style={{
          display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
          background: '#F79640', marginRight: 8, verticalAlign: 'middle',
          animation: 'kindra-pulse-dot 1.2s ease-in-out infinite',
        }} />
        Report generating…
      </span>
    );
  }
  const map = {
    in_progress:      { label: 'In progress',     bg: 'rgba(84,209,151,0.18)', text: '#7FE0C0' },
    complete:         { label: 'Complete',        bg: 'rgba(123,176,236,0.18)', text: '#9EC1E8' },
    ready_for_review: { label: '🔵 Ready to review', bg: 'rgba(236,124,36,0.22)', text: '#FFB469' },
    reviewed:         { label: 'Reviewed',        bg: 'rgba(84,209,151,0.18)', text: '#7FE0C0' },
  };
  const s = map[status] || { label: status || '—', bg: 'rgba(241,242,243,0.10)', text: '#F1F2F3' };
  return (
    <span
      data-testid={`os-status-pill-${status || 'unknown'}`}
      style={{
        display: 'inline-block',
        padding: '4px 10px', borderRadius: 999,
        fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
        fontFamily: 'Source Sans 3, system-ui, sans-serif',
        background: s.bg, color: s.text, whiteSpace: 'nowrap',
      }}
    >{s.label}</span>
  );
};

const DiscoveryStatusPill = ({ status, call_at }) => {
  const map = {
    not_started: { label: '⏳ Not started',          bg: 'rgba(247,150,64,0.20)', text: '#F79640' },
    in_progress: { label: '🟡 Started, not finished', bg: 'rgba(247,150,64,0.14)', text: '#F79640' },
    done:        { label: '✅ Discovery done',        bg: 'rgba(84,209,151,0.18)',  text: '#7FE0C0' },
  };
  const s = map[status] || { label: status || '—', bg: 'rgba(241,242,243,0.10)', text: '#F1F2F3' };
  return (
    <div className="flex flex-col gap-1">
      <span
        data-testid={`os-discovery-pill-${status || 'unknown'}`}
        style={{
          display: 'inline-block', padding: '4px 10px', borderRadius: 999,
          fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
          fontFamily: 'Source Sans 3, system-ui, sans-serif',
          background: s.bg, color: s.text, whiteSpace: 'nowrap', alignSelf: 'flex-start',
        }}
      >{s.label}</span>
      {call_at && (
        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: C.faint }}>
          Call: {new Date(call_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
    </div>
  );
};

const KpiTile = ({ label, value, accent, testid }) => (
  <div
    data-testid={`os-kpi-${testid || label.toLowerCase().replace(/\s+/g, '-')}`}
    className="rounded-2xl"
    style={{
      background: C.surface, border: `1px solid ${C.hair}`,
      padding: '20px 22px',
    }}
  >
    <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint }}>{label}</div>
    <div style={{ marginTop: 10, fontFamily: 'Fredoka, sans-serif', fontSize: 30, fontWeight: 600, color: C.icy, lineHeight: 1 }}>
      <CountUp value={value} duration={800} />{accent && <span style={{ marginLeft: 6, fontSize: 16, color: accent }}>●</span>}
    </div>
  </div>
);

// ─── LLM spend tile — one-glance monthly cap + trend + averages ───────────
// Reads from GET /api/admin/llm_spend/summary. Renders inline on the OS home
// so Michael can see whether the Universal Key is about to trip a budget
// window before it happens. Sparkline is CSS-only (no chart library).
const SettingRow = ({ endpoint, testid, icon, label, onText, offText }) => {
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}${endpoint}`, auth()).then((r) => alive && setOn(!!r.data.enabled)).catch(() => {});
    return () => { alive = false; };
  }, [endpoint]);
  if (on === null) return null;
  const flip = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}${endpoint}`, { enabled: !on }, auth());
      setOn(!!r.data.enabled);
    } catch (_e) { /* keep */ }
    setBusy(false);
  };
  return (
    <div data-testid={testid}
         style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 12px', padding: '10px 16px',
                   borderRadius: 12, flexWrap: 'wrap',
                   background: 'rgba(241,242,243,0.04)', border: '1px solid rgba(241,242,243,0.12)' }}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.icy }}>
        <b>{label} {on ? 'ON' : 'OFF'}</b>
        <span style={{ color: C.muted }}> — {on ? onText : offText}</span>
      </span>
      <button onClick={flip} disabled={busy} data-testid={`${testid}-toggle`}
              style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 999,
                        border: `1px solid ${C.hairBright}`, background: 'transparent', color: C.icy,
                        fontFamily: 'Source Sans 3, sans-serif', fontWeight: 600, fontSize: 12, cursor: busy ? 'wait' : 'pointer' }}>
        {busy ? '…' : on ? 'Turn OFF' : 'Turn ON'}
      </button>
    </div>
  );
};

const TestModeToggle = () => {
  const [state, setState] = useState(null); // {test_mode, test_recipient}
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/admin/settings/test-mode`, auth())
      .then((r) => alive && setState(r.data)).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!state) return null;
  const flip = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/settings/test-mode`, { enabled: !state.test_mode }, auth());
      setState({ ...state, test_mode: r.data.test_mode });
    } catch (_e) { /* leave as-is */ }
    setBusy(false);
  };
  return (
    <div data-testid="os-test-mode-row"
         style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 18px', padding: '12px 16px',
                   borderRadius: 12, flexWrap: 'wrap',
                   background: state.test_mode ? 'rgba(247,150,64,0.10)' : 'rgba(84,209,151,0.06)',
                   border: `1px solid ${state.test_mode ? 'rgba(247,150,64,0.5)' : 'rgba(84,209,151,0.3)'}` }}>
      <span style={{ fontSize: 15 }}>🧪</span>
      <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.icy }}>
        <b>Test mode {state.test_mode ? 'ON' : 'OFF'}</b>
        <span data-testid="os-test-mode-banner" style={{ color: C.muted }}>
          {state.test_mode
            ? ' — All client emails currently come to you instead of clients.'
            : ' — Client emails go to the real client address. Every send still needs your click.'}
        </span>
      </span>
      <button onClick={flip} disabled={busy} data-testid="os-test-mode-toggle"
              style={{ marginLeft: 'auto', padding: '7px 16px', borderRadius: 999,
                        border: `1px solid ${C.hairBright}`, background: 'transparent', color: C.icy,
                        fontFamily: 'Source Sans 3, sans-serif', fontWeight: 600, fontSize: 12, cursor: busy ? 'wait' : 'pointer' }}>
        {busy ? '…' : state.test_mode ? 'Turn OFF (real sends)' : 'Turn ON (redirect to me)'}
      </button>
    </div>
  );
};

const CheckinsDueTile = () => {
  const [due, setDue] = useState(0);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/admin/checkins/due-count`, auth())
      .then((r) => alive && setDue(r.data.due || 0))
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!due) return null;
  return (
    <Link to="/admin/os/blueprint" data-testid="os-checkins-due"
          style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 18px',
                    padding: '12px 16px', borderRadius: 12, textDecoration: 'none',
                    background: 'rgba(247,150,64,0.10)', border: '1px solid rgba(247,150,64,0.45)' }}>
      <span style={{ fontSize: 16 }}>📏</span>
      <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.icy }}>
        <b>{due}</b> blueprint check-in{due > 1 ? 's' : ''} due — score the plan{due > 1 ? 's' : ''} against real numbers
      </span>
      <span style={{ marginLeft: 'auto', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: '#F79640', fontWeight: 600 }}>
        Open library →
      </span>
    </Link>
  );
};

// ── Money KPI tile (Phase B/C) — read-only Xero roll-up ─────────────────────
const MoneyKpiTile = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [chaseOpen, setChaseOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async (force = false) => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/admin/xero/money_kpis${force ? '?force=1' : ''}`, auth());
      setD(r.data); setErr('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Xero KPIs unavailable.');
    }
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  if (err) return null; // fail-quiet (e.g. Xero not connected in preview)
  if (!d) return null;

  const money = (n) => `$${Number(n || 0).toLocaleString('en-AU')}`;
  const stat = (label, count, amount, color) => (
    <div style={{ flex: '1 1 140px', minWidth: 130 }}>
      <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: C.faint, margin: 0, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</p>
      <p style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 700, fontSize: 22, color, margin: '2px 0 0' }}>
        {money(amount)} <span style={{ fontSize: 12, color: C.faint, fontWeight: 500 }}>· {count} inv</span>
      </p>
    </div>
  );

  return (
    <div data-testid="os-money-kpi-tile" className="rounded-2xl mb-6"
         style={{ background: C.surface, border: '1px solid rgba(247,150,64,0.35)', padding: '18px 22px' }}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, fontWeight: 800, color: '#F79640', margin: 0, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
          Money · from Xero <span style={{ color: C.faint, fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(read-only — Xero sends &amp; collects)</span>
        </p>
        <button data-testid="os-money-refresh" onClick={() => load(true)} disabled={busy}
                style={{ background: 'transparent', border: `1px solid ${C.hair}`, borderRadius: 99, color: C.faint, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, padding: '4px 12px', cursor: 'pointer' }}>
          {busy ? '…' : '↻ Refresh'}
        </button>
      </div>
      <div className="flex flex-wrap gap-4">
        {stat('Drafts awaiting send', d.drafts?.count || 0, d.drafts?.total || 0, C.icy)}
        {stat('Sent · unpaid', d.unpaid?.count || 0, d.unpaid?.total_due || 0, '#F79640')}
        {stat('Paid this month', d.paid_month?.count || 0, d.paid_month?.total || 0, '#7FE0C0')}
      </div>
      {(d.chase || []).length > 0 && (
        <div className="mt-3">
          <button data-testid="os-money-chase-toggle" onClick={() => setChaseOpen(!chaseOpen)}
                  style={{ background: 'transparent', border: 'none', color: '#F79640', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
            {chaseOpen ? '▾' : '▸'} Who to chase ({d.chase.length})
          </button>
          {chaseOpen && (
            <div className="mt-2" data-testid="os-money-chase-list">
              {d.chase.map((c) => (
                <div key={c.invoice_id} className="flex items-center justify-between flex-wrap gap-2 py-1.5"
                     style={{ borderTop: `1px solid ${C.hair}` }}>
                  <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, color: C.icy }}>
                    {c.contact || '—'} <span style={{ color: C.faint }}>· {c.invoice_number}{c.due_date ? ` · due ${c.due_date}` : ''}</span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 700, fontSize: 13, color: '#F79640' }}>{money(c.amount_due)}</span>
                    <a href={c.link} target="_blank" rel="noreferrer" data-testid={`os-money-open-${c.invoice_number}`}
                       style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: '#7FE0C0', textDecoration: 'none', border: `1px solid ${C.hair}`, borderRadius: 99, padding: '3px 10px' }}>
                      Open in Xero ↗
                    </a>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const LlmSpendTile = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/llm_spend/summary`, auth());
        if (!cancelled) setD(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || 'Failed to load LLM spend.');
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (err) return null; // fail-quiet — the tile is glanceable, not blocking
  if (!d) {
    return (
      <div data-testid="os-llm-spend-tile" className="rounded-2xl mb-6"
        style={{ background: C.surface, border: `1px solid ${C.hair}`, padding: '18px 22px', color: C.faint, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13 }}>
        Loading LLM spend…
      </div>
    );
  }

  const pct = Math.max(0, Math.min(100, d.pct_of_cap || 0));
  // Bar colour steps calmly through the spectrum as spend climbs the cap.
  const barColor = pct < 60 ? '#54D197' : pct < 85 ? '#F79640' : '#EC7C24';
  const maxTrend = Math.max(1, ...((d.trend_30d || []).map(x => x.cents || 0)));

  // Group sources for a tidy "spent this month by kind" strip.
  const sources = Object.entries(d.by_source || {}).sort((a, b) => (b[1].cents || 0) - (a[1].cents || 0));

  return (
    <div
      data-testid="os-llm-spend-tile"
      className="rounded-2xl mb-6"
      style={{ background: C.surface, border: `1px solid ${C.hair}`, padding: '20px 22px' }}
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        {/* Left: month + cap headline */}
        <div style={{ minWidth: 220 }}>
          <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.faint }}>
            AI cost · this month
          </div>
          <div style={{ marginTop: 8, fontFamily: 'Fredoka, sans-serif', fontSize: 28, fontWeight: 600, color: C.icy, lineHeight: 1 }}>
            ${(d.spent_usd || 0).toFixed(2)}
            <span style={{ marginLeft: 8, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.muted, fontWeight: 400 }}>
              of ${(d.cap_usd || 0).toFixed(0)} cap
            </span>
          </div>
          {/* Cap progress bar */}
          <div style={{ marginTop: 10, height: 6, borderRadius: 999, background: 'rgba(241,242,243,0.08)', overflow: 'hidden', width: 220 }}>
            <div
              data-testid="os-llm-spend-bar"
              style={{ width: `${pct}%`, height: '100%', background: barColor, transition: 'width 400ms ease' }}
            />
          </div>
          <div style={{ marginTop: 8, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: C.muted }}>
            {pct.toFixed(1)}% used · ${(d.remaining_usd || 0).toFixed(2)} left
          </div>
        </div>

        {/* Middle: averages */}
        <div className="flex gap-6" style={{ minWidth: 260 }}>
          <div>
            <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint }}>
              Avg / blueprint
            </div>
            <div style={{ marginTop: 8, fontFamily: 'Fredoka, sans-serif', fontSize: 22, fontWeight: 600, color: C.icy, lineHeight: 1 }}>
              ${(d.avg_blueprint_usd || 0).toFixed(2)}
            </div>
            <div style={{ marginTop: 6, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: C.muted }}>
              {d.blueprints_this_month || 0} this month
            </div>
          </div>
          <div>
            <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint }}>
              Avg / report
            </div>
            <div style={{ marginTop: 8, fontFamily: 'Fredoka, sans-serif', fontSize: 22, fontWeight: 600, color: C.icy, lineHeight: 1 }}>
              ${(d.avg_report_usd || 0).toFixed(2)}
            </div>
            <div style={{ marginTop: 6, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: C.muted }}>
              {d.sessions_this_month || 0} this month
            </div>
          </div>
        </div>

        {/* Right: 30-day sparkline */}
        <div style={{ minWidth: 240, flex: '1 1 260px' }}>
          <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint }}>
            Last 30 days
          </div>
          <div
            data-testid="os-llm-spend-sparkline"
            className="flex items-end gap-[2px] mt-2"
            style={{ height: 44, width: '100%' }}
          >
            {(d.trend_30d || []).map((t) => {
              const h = maxTrend > 0 ? Math.max(2, Math.round((t.cents / maxTrend) * 44)) : 2;
              const bg = t.cents > 0 ? 'linear-gradient(180deg, #7BB0EC 0%, #C85A12 100%)' : 'rgba(241,242,243,0.08)';
              return (
                <div
                  key={t.date}
                  title={`${t.date} · $${t.usd.toFixed(2)}`}
                  style={{ flex: 1, height: `${h}px`, background: bg, borderRadius: 2 }}
                />
              );
            })}
          </div>
          {sources.length > 0 && (
            <div className="mt-2 flex gap-3 flex-wrap" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: C.muted }}>
              {sources.slice(0, 4).map(([name, v]) => {
                const plain = ({
                  'blueprint': 'Blueprint plans',
                  'blueprint-ask': 'Ask a follow-up',
                  'demo-chat': 'Demo chats',
                  'discover': 'Client chats',
                  'report': 'Reports',
                })[name] || name;
                return (
                  <span key={name} data-testid={`os-llm-spend-source-${name}`}>
                    <span style={{ color: C.faint, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{plain}</span>
                    {' '}
                    <span style={{ color: C.icy }}>${v.usd.toFixed(2)}</span>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Cockpit chrome — shared by all OS pages ───────────────────────────────
// Small SW-update toast — subscribes to service-worker `waiting` events (a
// new build was fetched but the current tab is still on the old bundle).
// One button: "Refresh now" swaps to the new build. Auto-shows when the
// sw.js `updatefound` fires with a waiting worker; dismissible.
const SwUpdateToast = () => {
  const [wait, setWait] = React.useState(null);
  const [dismissed, setDismissed] = React.useState(false);
  React.useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    let mounted = true;
    const armWaiting = (reg) => {
      if (!reg) return;
      if (reg.waiting && mounted) setWait(reg.waiting);
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller && mounted) {
            setWait(nw);
          }
        });
      });
    };
    navigator.serviceWorker.getRegistration().then(armWaiting).catch(() => {});
    // Reload once the new SW takes control
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (mounted) window.location.reload();
    });
    // Periodic poll for update — belt and braces for long-open tabs
    const int = setInterval(() => {
      navigator.serviceWorker.getRegistration().then((reg) => reg && reg.update()).catch(() => {});
    }, 5 * 60 * 1000);
    return () => { mounted = false; clearInterval(int); };
  }, []);
  if (!wait || dismissed) return null;
  const refresh = () => {
    try { wait.postMessage({ type: 'SKIP_WAITING' }); } catch { window.location.reload(); }
  };
  return (
    <div data-testid="sw-update-toast"
      style={{ position: 'fixed', bottom: 22, right: 22, zIndex: 4000,
        background: C.surface, border: `1px solid ${C.hairBright}`,
        borderRadius: 14, padding: '14px 18px', display: 'flex',
        gap: 14, alignItems: 'center', maxWidth: 420,
        boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
        fontFamily: 'Source Sans 3, sans-serif', fontSize: 13.5, color: C.icy }}>
      <span style={{ fontSize: 18 }}>✨</span>
      <span style={{ flex: 1 }}>A new version of TravelTrucks OS is ready.</span>
      <button data-testid="sw-refresh-btn" onClick={refresh}
        style={{ padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
          backgroundImage: C.spectrum, color: '#fff', border: 'none',
          fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, fontWeight: 700 }}>
        Refresh now
      </button>
      <button data-testid="sw-dismiss-btn" onClick={() => setDismissed(true)} aria-label="Dismiss"
        style={{ background: 'transparent', border: 'none', color: C.faint,
          fontSize: 18, cursor: 'pointer', padding: 4 }}>×</button>
    </div>
  );
};

export const Chrome = ({ children, crumb, activeTab, fullBleed = false }) => {
  const nav = useNavigate();
  // TravelTrucks OS ships DARK by default — pin the dark tokens on <html> while
  // any OS page is mounted, restoring the previous theme on unmount so the
  // (light) marketing site is unaffected.
  useEffect(() => {
    const el = document.documentElement;
    const had = { dark: el.classList.contains('dark'), kdark: el.classList.contains('k-dark') };
    el.classList.add('dark', 'k-dark');
    return () => {
      if (!had.dark) el.classList.remove('dark');
      if (!had.kdark) el.classList.remove('k-dark');
    };
  }, []);
  const logout = async () => { const { signOutEverywhere } = await import('./authRefresh'); await signOutEverywhere(); nav('/admin/login'); };
  const tabStyle = (active) => ({
    color: active ? C.icy : C.muted,
    fontFamily: 'Source Sans 3, sans-serif',
    fontSize: 13,
    fontWeight: active ? 600 : 500,
    textDecoration: 'none',
    padding: '6px 14px',
    borderRadius: 999,
    background: active ? 'rgba(241,242,243,0.07)' : 'transparent',
    border: active ? `1px solid ${C.hair}` : '1px solid transparent',
    letterSpacing: '0.02em',
  });
  return (
    <div className="min-h-screen" style={{ background: C.navy, color: C.icy, overflowX: 'hidden' }} data-testid="os-page">
      <SwUpdateToast />
      <KioskGlobalStyles />
      <KioskExitPill />
      <div className="kiosk-peek" />
      {/* Global keyframes for the "Report generating…" pulsing pill.
          Scoped by class name (kindra-pulse*) so no other page is affected. */}
      <style>{`
        @keyframes kindra-pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.55 } }
        @keyframes kindra-pulse-dot { 0%,100% { transform: scale(1); opacity: 1 } 50% { transform: scale(0.65); opacity: 0.55 } }
        /* Hide scrollbar for the mobile nav strip — swipeable but invisible */
        .kindra-nav-strip::-webkit-scrollbar { display: none; }
        .kindra-nav-strip { -ms-overflow-style: none; scrollbar-width: none; }
        /* BRAND STANDARD: default browser scrollbars must never appear anywhere
           in the OS shell. Same treatment as the Email Studio (.studio-scroll). */
        [data-testid="os-page"] * { scrollbar-width: thin; scrollbar-color: rgba(236,124,36,0.28) transparent; }
        @supports selector(::-webkit-scrollbar) {
          [data-testid="os-page"] * { scrollbar-width: auto; scrollbar-color: auto; }
          [data-testid="os-page"] *::-webkit-scrollbar { width: 6px; height: 6px; }
          [data-testid="os-page"] *::-webkit-scrollbar-track { background: transparent; }
          [data-testid="os-page"] *::-webkit-scrollbar-corner { background: transparent; }
          [data-testid="os-page"] *::-webkit-scrollbar-thumb { background: transparent; border-radius: 999px; }
          [data-testid="os-page"] *:hover::-webkit-scrollbar-thumb, [data-testid="os-page"] *:focus-within::-webkit-scrollbar-thumb { background: rgba(236,124,36,0.45); }
          [data-testid="os-page"] *::-webkit-scrollbar-thumb:hover { background: rgba(236,124,36,0.75); }
        }
        /* Document-level scrollbar (admin pages): same thin treatment */
        html { scrollbar-width: thin; scrollbar-color: rgba(236,124,36,0.28) transparent; }
        html::-webkit-scrollbar, body::-webkit-scrollbar { width: 6px; height: 6px; }
        html::-webkit-scrollbar-track, body::-webkit-scrollbar-track { background: transparent; }
        html::-webkit-scrollbar-thumb, body::-webkit-scrollbar-thumb { background: rgba(236,124,36,0.35); border-radius: 999px; }
        html::-webkit-scrollbar-thumb:hover, body::-webkit-scrollbar-thumb:hover { background: rgba(236,124,36,0.75); }
        /* Mobile: session-detail action pills reflow to full-width stacked
           buttons — labels stay readable, nothing overlaps or clips. */
        @media (max-width: 640px) {
          .kindra-action-row { width: 100%; }
          .kindra-action-row > button, .kindra-action-row > a {
            flex: 1 1 100%;
            display: inline-flex; align-items: center; justify-content: center;
            text-align: center; white-space: normal;
          }
        }
      `}</style>
      {/* Subtle radial glows for depth — pure decoration */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: 'radial-gradient(900px 600px at -10% -10%, rgba(236,124,36,0.10), transparent 60%),'
                  + 'radial-gradient(900px 600px at 110% 10%, rgba(84,209,151,0.10), transparent 60%)' }} />
      <header className="sticky top-0 z-30"
              style={{ background: 'linear-gradient(180deg, rgba(16,17,19,0.72), rgba(16,17,19,0.42))',
                       backdropFilter: 'blur(26px) saturate(175%)', WebkitBackdropFilter: 'blur(26px) saturate(175%)',
                       borderBottom: `1px solid ${C.hair}`, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10)' }}>
        {/* Top row: logo + right actions. Nav moves to its own row on mobile
            (swipeable pill strip). Prevents 6 tabs + logo + actions from
            causing page-level horizontal overflow on iPhone/iPad. */}
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link to="/admin/os" className="flex items-center gap-3 min-w-0" data-testid="os-logo">
            <V2Logo variant="main" tone="icy" height={42} />
            <span style={{ display: 'inline-block', width: 1, height: 18, background: C.hair }} />
            <span className="text-[11px] tracking-[0.28em] uppercase font-semibold" style={{ color: 'var(--tt-orange)', fontFamily: 'var(--ff-caps)' }}>Company OS</span>
          </Link>
          <div className="flex items-center gap-4 ml-auto">
            <KioskControls />
            <Link to="/admin" data-testid="os-admin-back"
                  style={{ color: C.muted, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, textDecoration: 'none' }}>
              ← Admin
            </Link>
            <button onClick={logout} data-testid="os-logout"
                    style={{ color: C.muted, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, background: 'transparent', border: 'none', cursor: 'pointer' }}>
              Sign out
            </button>
          </div>
        </div>
        {/* Nav strip — always horizontally scrollable so it never causes
            page-level overflow. Desktop shows all tabs in one row anyway. */}
        <nav className="tt-nav-strip max-w-[1280px] mx-auto px-4 md:px-6 pb-3 flex items-center gap-1.5"
             style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
          <Link to="/admin" data-testid="os-tab-desk" style={{ ...tabStyle(activeTab === 'desk'), flex: '0 0 auto' }}
                title="Base Camp — what needs you right now">
            🏠 The Desk
          </Link>
          <Link to="/admin/clients" data-testid="os-tab-clients" style={{ ...tabStyle(activeTab === 'clients'), flex: '0 0 auto' }}
                title="Customers — everything per customer & vehicle">
            Customers
          </Link>
          <Link to="/admin/quotes" data-testid="os-tab-quotes" style={{ ...tabStyle(activeTab === 'quotes'), flex: '0 0 auto' }}
                title="Build quotes — compose, send, track, win">
            Quotes
          </Link>
          <Link to="/admin/projects" data-testid="os-tab-projects" style={{ ...tabStyle(activeTab === 'projects'), flex: '0 0 auto' }}
                title="Every build in progress">
            ◈ Builds
          </Link>
          <Link to="/admin/connectors" data-testid="os-tab-connectors" style={{ ...tabStyle(activeTab === 'connectors'), flex: '0 0 auto' }}
                title="Plug in Xero, Fieldy, Microsoft 365, GoHighLevel, Workflow Max, Stripe">
            🔌 Connectors
          </Link>
          <Link to="/admin/os/help" data-testid="os-tab-help" style={{ ...tabStyle(activeTab === 'help'), flex: '0 0 auto' }}
                title="How TravelTrucks OS works — one screen for new staff">
            ❔ How this works
          </Link>
        </nav>
        {crumb && (
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 pb-3 text-[12px]" style={{ color: C.faint, fontFamily: 'Source Sans 3, sans-serif' }}>
            {crumb}
          </div>
        )}
      </header>
      <main className={fullBleed ? 'relative z-10' : 'relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 py-8'}>{children}</main>
      {activeTab !== 'desk' && <V2FirstRunTour />}
      <SessionKeeper />
    </div>
  );
};

// ─── Queue view (the landing) ──────────────────────────────────────────────
export const V2AdminOSQueuePage = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all'); // all | ready | reviewed | in_progress
  const [showArchived, setShowArchived] = useState(false);
  const [archivedRows, setArchivedRows] = useState(null);
  const [toast, setToast] = useState(null);
  const [rowMenu, setRowMenu] = useState(null); // session_id whose kebab is open
  const say = (m) => { setToast(m); setTimeout(() => setToast(null), 3800); };
  const nav = useNavigate();

  useEffect(() => {
    if (!tokenStore.get()) return;
    let cancelled = false;
    let pollTimer = null;

    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/os/queue`, auth());
        if (cancelled) return;
        setData(r.data);
        // Adaptive polling: 6s while anything is generating (or failed),
        // 25s otherwise. Guarantees the queue never looks stale, and
        // flips to Ready-for-review the moment a report lands — no
        // manual refresh required.
        const anyGenerating = (r.data?.sessions || []).some(
          (s) => (s.status === 'complete' && !s.has_report) || s.report_error,
        );
        const nextMs = anyGenerating ? 6000 : 25000;
        if (!cancelled) pollTimer = setTimeout(load, nextMs);
      } catch (e) {
        if (e?.response?.status === 401) { tokenStore.clear(); const r=encodeURIComponent(window.location.pathname+window.location.search); window.location.assign('/admin/login?returnTo='+r); return; }
        setErr(e?.response?.data?.detail || 'Failed to load queue.');
        // Even on error, keep retrying every 25s so a transient blip
        // doesn't freeze the queue for the day.
        if (!cancelled) pollTimer = setTimeout(load, 25000);
      }
    };
    load();
    return () => { cancelled = true; if (pollTimer) clearTimeout(pollTimer); };
  }, [nav]);

  const sessions = data?.sessions || [];
  const filtered = useMemo(() => {
    if (filter === 'all') return sessions;
    if (filter === 'ready') return sessions.filter((s) => s.status === 'ready_for_review');
    if (filter === 'in_progress') return sessions.filter((s) => s.status === 'in_progress');
    if (filter === 'reviewed') return sessions.filter((s) => s.status === 'reviewed');
    if (filter === 'generating') return sessions.filter(
      (s) => (s.status === 'complete' && !s.has_report) || !!s.report_error,
    );
    if (filter === 'needs_nudge') {
      // Booked clients with an upcoming call AND discovery not_started or in_progress.
      return sessions.filter((s) => s.linked && (s.discovery_status === 'not_started' || s.discovery_status === 'in_progress'));
    }
    return sessions;
  }, [sessions, filter]);

  // Manual retry for the "Report failed" row action. Fires the same
  // regenerate endpoint the detail page uses, then triggers a poll.
  const regenerateRow = async (session_id) => {
    try {
      await axios.post(`${API}/admin/os/sessions/${session_id}/regenerate`, {}, auth());
      const r = await axios.get(`${API}/admin/os/queue`, auth());
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Regenerate failed.');
    }
  };

  // ── Client Reports CRUD (archive / restore / destroy) ──────────
  const reloadQueue = async () => {
    try {
      const [q, a] = await Promise.all([
        axios.get(`${API}/admin/os/queue`, auth()),
        showArchived ? axios.get(`${API}/admin/os/queue/archived`, auth()) : Promise.resolve(null),
      ]);
      setData(q.data);
      if (a) setArchivedRows(a.data.sessions || []);
    } catch (e) { /* handled elsewhere */ }
  };
  const loadArchived = async () => {
    try {
      const a = await axios.get(`${API}/admin/os/queue/archived`, auth());
      setArchivedRows(a.data.sessions || []);
    } catch (e) { say('Could not load archive'); }
  };
  const rowArchive = async (sid, name) => {
    try {
      await axios.post(`${API}/admin/os/sessions/${sid}/archive`, {}, auth());
      say(`🗄 Archived · ${name || 'report'}`);
      await reloadQueue();
    } catch (e) { say('Archive failed: ' + (e?.response?.data?.detail || 'unknown')); }
    setRowMenu(null);
  };
  const rowRestore = async (sid, name) => {
    try {
      await axios.post(`${API}/admin/os/sessions/${sid}/restore`, {}, auth());
      say(`↺ Restored · ${name || 'report'}`);
      await reloadQueue();
    } catch (e) { say('Restore failed: ' + (e?.response?.data?.detail || 'unknown')); }
    setRowMenu(null);
  };
  const rowDestroy = async (sid, name) => {
    if (!window.confirm(`Permanently delete "${name || 'this report'}"?\nThis cannot be undone. Linked quotes will be archived.`)) {
      setRowMenu(null); return;
    }
    try {
      const r = await axios.delete(`${API}/admin/os/sessions/${sid}?confirm=DELETE`, auth());
      say(`🗑 Deleted · ${r.data.cascaded_quotes_archived || 0} linked quote(s) archived`);
      await reloadQueue();
    } catch (e) { say('Delete failed: ' + (e?.response?.data?.detail || 'unknown')); }
    setRowMenu(null);
  };

  // Close kebab menu on outside click
  useEffect(() => {
    if (!rowMenu) return;
    const close = (e) => {
      if (!(e.target.closest && e.target.closest('[data-cq-kebab]'))) setRowMenu(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [rowMenu]);

  // Load archived rows whenever the drawer is toggled on
  useEffect(() => { if (showArchived) loadArchived(); /* eslint-disable-next-line */ }, [showArchived]);

  if (!tokenStore.get()) return <Navigate to="/admin/login" replace />;

  return (
    <Chrome activeTab="queue">
      {/* Headline */}
      <Lively fast className="flex items-end justify-between flex-wrap gap-4 mb-4">
        <div>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', color: C.faint, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Kindra OS · Client reports</p>
          <h1 style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy, fontSize: 'clamp(28px,4vw,38px)', lineHeight: 1.1, marginTop: 6 }}>
            What needs your eyes today
          </h1>
          <p data-testid="os-home-explainer" style={{ fontFamily: 'Source Sans 3, sans-serif', color: C.muted, fontSize: 14, marginTop: 8, maxWidth: 640 }}>
            This is where finished client chats become reports. Open one, review it, then decide whether to send a free preview or the full report — nothing goes to the client until you tap a confirmation button.
          </p>
        </div>
      </Lively>

      {/* LLM spend tile — one-glance cap + averages + 30-day trend */}
      <LlmSpendTile />
      <MoneyKpiTile />

      <CheckinsDueTile />

      <TestModeToggle />

      <SettingRow endpoint="/admin/settings/owner-notify" testid="os-owner-notify-row" icon="🔔"
        label="Owner notifications"
        onText="you get an email for new leads, finished reports and bookings."
        offText="no owner emails fire (leads/reports/bookings arrive silently)." />

      <SettingRow endpoint="/admin/settings/daily-digest" testid="os-daily-digest-row" icon="🌅"
        label="Daily 7am digest"
        onText="one summary email each morning — yesterday's leads, chats, bookings and due check-ins."
        offText="no morning summary email." />

      {/* KPI strip */}
      {data?.counts && (
        <Lively fast staggerChildren className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="os-kpi-strip">
          <KpiTile label="Ready to review" value={data.counts.ready_for_review} accent="#EC7C24" />
          <KpiTile label="Generating" value={data.counts.generating ?? 0} accent="#F79640" testid="generating" />
          <KpiTile label="Needs a nudge"   value={data.counts.needs_nudge ?? 0} accent="#F79640" testid="needs-nudge" />
          <KpiTile label="In progress"      value={data.counts.in_progress} />
          <KpiTile label="Reviewed"          value={data.counts.reviewed} />
          <KpiTile label="Booked clients"    value={data.counts.linked} />
        </Lively>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="os-filters">
        {[
          { k: 'all',         label: 'All' },
          { k: 'ready',       label: 'Ready to review' },
          { k: 'generating',  label: 'Generating' },
          { k: 'needs_nudge', label: 'Needs a nudge' },
          { k: 'in_progress', label: 'In progress' },
          { k: 'reviewed',    label: 'Reviewed' },
        ].map((f) => (
          <button
            key={f.k}
            data-testid={`os-filter-${f.k}`}
            onClick={() => { setFilter(f.k); setShowArchived(false); }}
            style={{
              padding: '7px 14px', borderRadius: 999,
              fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, fontWeight: 600,
              background: (!showArchived && filter === f.k) ? 'rgba(241,242,243,0.10)' : 'transparent',
              color: (!showArchived && filter === f.k) ? C.icy : C.muted,
              border: `1px solid ${(!showArchived && filter === f.k) ? C.hairBright : C.hair}`,
              cursor: 'pointer',
            }}
          >{f.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        <button data-testid="os-toggle-archive" onClick={() => setShowArchived(!showArchived)}
          style={{
            padding: '7px 14px', borderRadius: 999,
            fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, fontWeight: 600,
            background: showArchived ? 'rgba(241,242,243,0.10)' : 'transparent',
            color: showArchived ? C.icy : C.muted,
            border: `1px solid ${showArchived ? C.hairBright : C.hair}`,
            cursor: 'pointer',
          }}>
          {showArchived ? '← Active reports' : '🗄 Archive drawer'}
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div data-testid="os-toast" style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 3000, background: C.surface, border: `1px solid ${C.hairBright}`, color: C.icy,
          padding: '11px 24px', borderRadius: 999, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, fontWeight: 600,
          boxShadow: '0 14px 40px rgba(0,0,0,0.5)' }}>{toast}</div>
      )}

      {/* Archived drawer */}
      {showArchived && (
        <Lively fast className="rounded-2xl overflow-hidden mb-6" data-testid="os-archive-drawer"
          style={{ background: C.surface, border: `1px solid ${C.hair}` }}>
          <div className="px-5 py-3" style={{ borderBottom: `1px solid ${C.hair}`, fontFamily: 'Source Sans 3, sans-serif',
            fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.faint }}>
            Archived client reports {archivedRows ? `· ${archivedRows.length}` : ''}
          </div>
          {archivedRows === null && (
            <div className="px-5 py-12 text-center" style={{ color: C.faint, fontFamily: 'Source Sans 3, sans-serif' }}>Loading…</div>
          )}
          {archivedRows && archivedRows.length === 0 && (
            <div className="px-5 py-12 text-center" data-testid="os-archive-empty"
              style={{ color: C.faint, fontFamily: 'Source Sans 3, sans-serif' }}>Archive is empty.</div>
          )}
          {(archivedRows || []).map((s) => (
            <div key={s.session_id} data-testid={`os-arch-row-${s.session_id}`}
              className="grid grid-cols-12 px-5 py-4 items-center"
              style={{ borderTop: `1px solid ${C.hair}`, color: C.icy }}>
              <div className="col-span-5">
                <div style={{ fontFamily: 'Fredoka, sans-serif', fontSize: 15, fontWeight: 600 }}>
                  {s.business_name || s.contact_name || 'Anonymous session'}
                </div>
                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, color: C.muted, marginTop: 3 }}>
                  {s.headline ? s.headline.slice(0, 130) : '—'}
                </div>
              </div>
              <div className="col-span-3" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: C.muted }}>
                archived {fmtDate(s.archived_at)}
              </div>
              <div className="col-span-1" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.muted }}>
                {s.message_count || 0}
              </div>
              <div className="col-span-3 flex gap-2 justify-end">
                <button data-testid={`os-arch-restore-${s.session_id}`}
                  onClick={() => rowRestore(s.session_id, s.business_name)}
                  style={{ padding: '6px 12px', borderRadius: 999,
                    background: 'transparent', color: C.icy,
                    border: `1px solid ${C.hairBright}`,
                    fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
                  ↺ Restore
                </button>
                <button data-testid={`os-arch-destroy-${s.session_id}`}
                  onClick={() => rowDestroy(s.session_id, s.business_name)}
                  style={{ padding: '6px 12px', borderRadius: 999,
                    background: 'transparent', color: '#FFB469',
                    border: `1px solid rgba(236,124,36,0.35)`,
                    fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
                  🗑 Delete
                </button>
              </div>
            </div>
          ))}
        </Lively>
      )}

      {/* Table */}
      {!showArchived && (
      <Lively fast className="rounded-2xl overflow-hidden" style={{ background: C.surface, border: `1px solid ${C.hair}` }}>
        <div className="grid grid-cols-12 px-5 py-3" style={{ borderBottom: `1px solid ${C.hair}`,
              fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.faint }}>
          <div className="col-span-4">Client</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-3">Headline</div>
          <div className="col-span-1">Msgs</div>
          <div className="col-span-2 text-right">Last activity</div>
        </div>
        {err && <div className="px-5 py-4" style={{ color: '#FFB469', fontFamily: 'Source Sans 3, sans-serif', fontSize: 14 }}>{err}</div>}
        {!data && !err && (
          <div className="px-5 py-12 text-center" style={{ color: C.faint, fontFamily: 'Source Sans 3, sans-serif' }}>Loading…</div>
        )}
        {data && filtered.length === 0 && filter !== 'ready' && (
          <div className="px-5 py-12 text-center" data-testid="os-empty"
               style={{ color: C.faint, fontFamily: 'Source Sans 3, sans-serif' }}>
            Nothing in this filter yet. Try &quot;All&quot; to see everything.
          </div>
        )}
        {data && filtered.length === 0 && filter === 'ready' && (
          <div
            className="px-6 py-12 text-center"
            data-testid="os-queue-empty-ready"
            style={{ borderTop: `1px solid ${C.hair}` }}
          >
            <div style={{ fontFamily: 'Fredoka, sans-serif', fontSize: 18, fontWeight: 600, color: C.icy }}>
              Nothing new to review right now.
            </div>
            <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 14, color: C.muted, marginTop: 8 }}>
              When someone finishes a Leak Check chat on the site, their report will land here. You&apos;ll get to review it before anything is sent.
            </div>
          </div>
        )}
        {filtered.map((s, idx) => (
          <div key={s.session_id} style={{ position: 'relative', borderTop: `1px solid ${C.hair}` }}>
          <Link
            to={`/admin/os/sessions/${s.session_id}`}
            data-testid={idx === 0 ? 'os-first-row' : `os-row-${s.session_id}`}
            className="grid grid-cols-12 px-5 py-4 items-center"
            style={{ color: C.icy, textDecoration: 'none' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(241,242,243,0.03)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <div className="col-span-4">
              <div style={{ fontFamily: 'Fredoka, sans-serif', fontSize: 16, fontWeight: 600 }}>
                {s.business_name || (s.linked ? 'Booked client (no business name yet)' : 'In-progress session')}
              </div>
              <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.muted, marginTop: 2 }}>
                {/* When business name is missing, surface the first user
                    turn as a snippet so Michael can tell rows apart at
                    a glance. Falls back to the original "Booked / —"
                    pattern when no transcript yet. */}
                {s.contact_name
                  ? <>{s.contact_name}{s.linked ? ' · Booked' : ''}</>
                  : (s.first_user_message
                      ? <span style={{ fontStyle: 'italic' }}>“{s.first_user_message}”</span>
                      : (s.linked ? 'Booked' : '—'))}
              </div>
            </div>
            <div className="col-span-2">
              {/* For BOOKED clients, show the discovery-status pill so
                  Michael sees at a glance who needs a nudge before their
                  call. Falls back to the normal session-status pill for
                  anonymous sessions. Overrides:
                    · status='complete' && !has_report → pulsing "Generating…"
                    · report_error → "⚠ Report failed" + Regenerate button */}
              {s.linked && !s.report_error && !(s.status === 'complete' && !s.has_report) ? (
                <DiscoveryStatusPill status={s.discovery_status} call_at={s.call_at} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                  <StatusPill status={s.status} hasReport={s.has_report} reportError={s.report_error} />
                  {s.report_error && (
                    <button
                      data-testid={`os-row-regenerate-${s.session_id}`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); regenerateRow(s.session_id); }}
                      style={{
                        padding: '3px 10px', borderRadius: 999,
                        background: 'rgba(84,209,151,0.16)', color: '#7FE0C0',
                        border: `1px solid rgba(84,209,151,0.35)`,
                        fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >↻ Regenerate</button>
                  )}
                </div>
              )}
            </div>
            <div className="col-span-3" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.muted, lineHeight: 1.45 }}>
              {s.headline ? s.headline.slice(0, 130) + (s.headline.length > 130 ? '…' : '') : <span style={{ color: C.faint }}>—</span>}
            </div>
            <div className="col-span-1" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 14, color: C.muted }}>{s.message_count || 0}</div>
            <div className="col-span-2 text-right" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.muted, paddingRight: 40 }}>
              {fmtDate(s.completed_at || s.created_at)}
            </div>
          </Link>
          {/* Kebab menu overlaid on the row (stops navigation) */}
          <button data-cq-kebab data-testid={`os-row-menu-${s.session_id}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation();
              setRowMenu(rowMenu === s.session_id ? null : s.session_id); }}
            aria-label="Row actions"
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              width: 32, height: 32, borderRadius: 999, cursor: 'pointer',
              background: 'transparent', border: `1px solid ${C.hair}`, color: C.icy,
              fontFamily: 'Source Sans 3, sans-serif', fontSize: 16, lineHeight: 1, display: 'grid', placeItems: 'center' }}>⋯</button>
          {rowMenu === s.session_id && (
            <div data-cq-kebab onClick={(e) => e.stopPropagation()}
              style={{ position: 'absolute', right: 12, top: '100%', marginTop: -6, zIndex: 40,
                background: C.surface, border: `1px solid ${C.hairBright}`, borderRadius: 12,
                padding: 6, boxShadow: '0 20px 60px rgba(0,0,0,0.6)', minWidth: 200 }}>
              <button data-testid={`os-row-archive-${s.session_id}`}
                onClick={() => rowArchive(s.session_id, s.business_name)}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px',
                  background: 'transparent', border: 'none', color: C.icy,
                  fontFamily: 'Source Sans 3, sans-serif', fontSize: 13.5, fontWeight: 600, borderRadius: 8, cursor: 'pointer' }}>
                🗄 Archive
              </button>
            </div>
          )}
          </div>
        ))}
      </Lively>
      )}
    </Chrome>
  );
};

// ─── Detail view ───────────────────────────────────────────────────────────
const Card = ({ title, children, right, testId }) => (
  <Lively as="section" fast
    data-testid={testId}
    className="rounded-2xl mb-5"
    style={{ background: C.surface, border: `1px solid ${C.hair}` }}
  >
    <header className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${C.hair}` }}>
      <h2 style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy, fontSize: 18, letterSpacing: '-0.01em' }}>{title}</h2>
      {right}
    </header>
    <div className="px-6 py-5">{children}</div>
  </Lively>
);

const LeakDot = ({ area }) => (
  <span style={{
    display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
    background: LEAK_COLOR[area] || C.faint, marginRight: 8, verticalAlign: 'middle',
  }} />
);

const SeverityPill = ({ sev }) => {
  const s = SEV[sev] || SEV.none;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 999,
      fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
    }}>{s.label}</span>
  );
};

const BusinessMap = ({ stages }) => {
  if (!stages || stages.length === 0) {
    return <div style={{ color: C.faint, fontFamily: 'Source Sans 3, sans-serif', fontSize: 14 }}>No business map generated.</div>;
  }
  return (
    <div className="overflow-x-auto pb-2" data-testid="os-business-map">
      <div className="flex items-stretch gap-3 min-w-max">
        {stages.map((s, i) => {
          const isLeak = s.state === 'leaking';
          const col = isLeak ? (LEAK_COLOR[s.leak_area] || '#EC7C24') : C.hairBright;
          return (
            <React.Fragment key={s.id || i}>
              <div
                className="rounded-xl px-4 py-3"
                style={{
                  minWidth: 180, maxWidth: 240,
                  background: isLeak ? `${col}1a` : 'rgba(241,242,243,0.04)',
                  border: `1px solid ${isLeak ? `${col}66` : C.hair}`,
                }}
              >
                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
                              color: isLeak ? col : C.faint, marginBottom: 6, fontWeight: 700 }}>
                  {isLeak ? 'Leaking' : 'Clean'}
                </div>
                <div style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy, fontSize: 14, lineHeight: 1.25 }}>{s.label}</div>
                {s.note && <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>{s.note}</div>}
                {s.source_quote && (
                  <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: C.faint, marginTop: 8, fontStyle: 'italic', lineHeight: 1.5 }}>
                    “{s.source_quote}”
                  </div>
                )}
              </div>
              {i < stages.length - 1 && (
                <div className="flex items-center" aria-hidden>
                  <svg width="22" height="14" viewBox="0 0 22 14"><path d="M1 7h18m0 0l-5-5m5 5l-5 5" stroke={C.hairBright} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ─── Transcript card — full discovery chat, internal-only ─────────────────
// Surfaces the raw back-and-forth between Kindra and the client so Michael
// can read exactly what was said before the call. Collapsible so it doesn't
// dominate the page; defaults open. Never shown to clients.
const TranscriptCard = ({ transcript, businessName }) => {
  const [open, setOpen] = React.useState(true);
  const turns = Array.isArray(transcript) ? transcript : [];
  const fmtTs = (ts) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };
  return (
    <Card title={`Discovery chat transcript${businessName ? ' · ' + businessName : ''}`}
          testId="os-transcript-card"
          right={
            <button onClick={() => setOpen((v) => !v)} data-testid="os-transcript-toggle"
                    style={{ background: 'transparent', border: `1px solid ${C.hair}`, color: C.muted,
                              fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, padding: '5px 12px', borderRadius: 999,
                              cursor: 'pointer', letterSpacing: '0.06em' }}>
              {open ? 'Collapse' : `Expand · ${turns.length} turns`}
            </button>
          }>
      {!open ? (
        <p style={{ color: C.faint, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13 }}>
          {turns.length} {turns.length === 1 ? 'turn' : 'turns'} hidden — click Expand to read.
        </p>
      ) : turns.length === 0 ? (
        <p style={{ color: C.faint, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13 }}>
          No transcript captured for this session yet.
        </p>
      ) : (
        <div className="flex flex-col gap-3" data-testid="os-transcript-list"
             style={{ maxHeight: 600, overflowY: 'auto', paddingRight: 6 }}>
          {turns.map((t, i) => {
            const isKindra = t.role === 'assistant';
            const text = (t.content ?? t.text ?? '').toString();
            return (
              <div key={i} data-testid={`os-transcript-turn-${i}`}
                   style={{
                     display: 'grid', gridTemplateColumns: '70px 1fr', gap: 14,
                     padding: '10px 12px', borderRadius: 10,
                     background: isKindra ? 'rgba(123,176,236,0.05)' : 'rgba(241,242,243,0.02)',
                     border: `1px solid ${isKindra ? 'rgba(123,176,236,0.15)' : C.hair}`,
                   }}>
                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, color: isKindra ? '#7BB0EC' : C.faint,
                              letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, paddingTop: 2 }}>
                  {isKindra ? 'Kindra' : 'Client'}
                  <div style={{ fontWeight: 400, letterSpacing: 0, textTransform: 'none', fontSize: 10,
                                 color: C.faint, marginTop: 2 }}>{fmtTs(t.ts)}</div>
                </div>
                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 14, color: C.icy, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {text}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export const V2AdminOSDetailPage = () => {
  const { sessionId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [actionErr, setActionErr] = useState('');
  const [teaser, setTeaser] = useState(null);   // {ready, hits, fields}
  const [tBusy, setTBusy] = useState(false);
  const [tEdits, setTEdits] = useState(null);   // editable rows when auto-clean fails
  const [busy, setBusy] = useState(false);
  const [editedReport, setEditedReport] = useState(null);   // becomes truthy on first edit
  const [reviewNotes, setReviewNotes] = useState('');
  const [showTranscript, setShowTranscript] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      // Trim defensively — HubSpot Notes / emails / Slack occasionally append
      // stray chars when the URL is copy-pasted. Backend also normalises.
      const sid = (sessionId || '').trim().replace(/[\s/\u200b]/g, '');
      const r = await axios.get(`${API}/admin/os/sessions/${encodeURIComponent(sid)}`, auth());
      setData(r.data);
      setReviewNotes(r.data?.session?.review_notes || '');
      // Teaser health is a side-panel concern — NEVER blocks the session.
      axios.get(`${API}/admin/os/sessions/${encodeURIComponent(sid)}/teaser_preview`, auth())
        .then((t) => setTeaser(t.data))
        .catch(() => setTeaser(null));
    } catch (e) {
      if (e?.response?.status === 401) { tokenStore.clear(); const r=encodeURIComponent(window.location.pathname+window.location.search); window.location.assign('/admin/login?returnTo='+r); return; }
      const detail = e?.response?.data?.detail;
      if (detail === 'session_not_found') {
        setErr(`We couldn't find that session — it may have been deleted or the link is malformed. Sessions live at /admin/os.`);
      } else {
        setErr(detail || 'Failed to load session.');
      }
    }
  };
  useEffect(() => { if (tokenStore.get()) load();   }, [sessionId]);

  if (!tokenStore.get()) return <Navigate to="/admin/login" replace />;
  if (err) return (
    <Chrome activeTab="queue">
      <div className="rounded-2xl px-6 py-10" style={{ background: C.surface, border: `1px solid ${C.hair}` }}
           data-testid="os-detail-error">
        <h2 style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy, fontSize: 22, marginBottom: 10 }}>
          Couldn&apos;t open this session
        </h2>
        <p style={{ color: C.muted, fontFamily: 'Source Sans 3, sans-serif', fontSize: 14, lineHeight: 1.6, marginBottom: 18 }}>{err}</p>
        <Link to="/admin/os" data-testid="os-detail-error-back-to-queue"
              style={{ display: 'inline-block', padding: '10px 18px', borderRadius: 999,
                        border: `1px solid ${C.hairBright}`, background: 'transparent',
                        color: C.icy, fontFamily: 'Source Sans 3, sans-serif', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
          ← Back to the queue
        </Link>
      </div>
    </Chrome>
  );
  if (!data) return <Chrome activeTab="queue"><div style={{ color: C.faint, fontFamily: 'Source Sans 3, sans-serif' }}>Loading…</div></Chrome>;

  const s = data.session;
  const report = editedReport || s.report || {};
  const editing = !!editedReport;

  const startEditing = () => setEditedReport(JSON.parse(JSON.stringify(s.report || {})));
  const cancelEditing = () => setEditedReport(null);

  const updateField = (path, value) => {
    setEditedReport((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let cur = next;
      const keys = path.slice(0, -1);
      for (const k of keys) cur = cur[k];
      cur[path[path.length - 1]] = value;
      return next;
    });
  };

  const teaserDirty = !!(teaser && teaser.ready && (teaser.hits || []).length > 0);

  const teaserHitsPlain = (hits) => {
    const TOOLS = ['servicem8', 'tradify', 'xero', 'hubspot', 'twilio', 'simpro', 'aroflo', 'fergus', 'quickbooks', 'myob'];
    const out = []; let digits = 0;
    (hits || []).forEach((h) => {
      if (h.startsWith('raw-digit')) digits += 1;
      else if (h === 'dollar-sign') out.push('included a dollar amount');
      else if (TOOLS.includes(h)) out.push(`mentioned a tool name: ${h.charAt(0).toUpperCase() + h.slice(1)}`);
      else out.push(`included an internal term: “${h}”`);
    });
    if (digits) out.push(`included ${digits} unsourced number${digits > 1 ? 's' : ''}`);
    return out;
  };

  const regenTeaser = async () => {
    setTBusy(true); setActionErr('');
    try {
      const r = await axios.post(`${API}/admin/os/sessions/${sessionId}/regenerate_teaser`, {}, auth());
      setTeaser({ ready: true, hits: r.data.hits, fields: r.data.fields });
      setTEdits(r.data.ok ? null : r.data.fields);
    } catch (e) {
      setActionErr('Could not regenerate the preview — give it another go.');
    }
    setTBusy(false);
  };

  const rescanTeaser = async () => {
    setTBusy(true); setActionErr('');
    try {
      const overrides = {};
      (tEdits || []).forEach((r) => { overrides[r.leak_area] = { title: r.title, pain: r.pain, outcome: r.outcome }; });
      const r = await axios.post(`${API}/admin/os/sessions/${sessionId}/teaser_overrides`, { overrides }, auth());
      setTeaser({ ready: true, hits: r.data.hits, fields: r.data.fields });
      if (r.data.ok) setTEdits(null);
    } catch (e) {
      setActionErr('Rescan failed — give it another go.');
    }
    setTBusy(false);
  };

  const save = async ({ markReviewed = false } = {}) => {
    setBusy(true);
    try {
      const body = {};
      if (editedReport) body.report = editedReport;
      if (reviewNotes !== (s.review_notes || '')) body.review_notes = reviewNotes;
      if (markReviewed) body.review_status = 'reviewed';
      const r = await axios.patch(`${API}/admin/os/sessions/${sessionId}`, body, auth());
      setData((d) => ({ ...d, session: r.data.session }));
      setEditedReport(null);
    } catch (e) {
      setActionErr(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    if (!confirm('Re-run the AI auto-report? Current edits will be preserved as a version.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/os/sessions/${sessionId}/regenerate`, {}, auth());
      // Poll once after 8s; user can also just refresh.
      setTimeout(() => load(), 8000);
    } finally { setBusy(false); }
  };

  const createQuote = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/admin/os/sessions/${sessionId}/quote`, {}, auth());
      const q = r.data?.quote;
      if (q?.quote_id) nav(`/admin/os/quotes/${q.quote_id}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not create quote.');
    } finally { setBusy(false); }
  };

  const sendDiscoveryReminder = async () => {
    // Explicit confirm — never automatic. Michael owns the click.
    if (!confirm('Send the discovery reminder email to this client now?')) return;
    setBusy(true); setErr('');
    try {
      const r = await axios.post(
        `${API}/admin/os/sessions/${sessionId}/send_discovery_reminder`,
        {},
        auth(),
      );
      alert(`Reminder sent to ${r.data.emailed_to}.`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setActionErr(d === 'no_recipient_email' ? 'No recipient email on file for this client.'
            : d === 'discovery_already_done' ? 'This client has already finished their discovery.'
            : d === 'not_a_booked_session' ? 'Reminders only work for booked-client sessions.'
            : d || 'Could not send the reminder.');
    } finally { setBusy(false); }
  };

  const sendClientReport = async () => {
    // Two-step gate: preview button opens the private teaser page in a
    // new tab so Michael eyeballs it FIRST (via the sibling "Preview
    // teaser page" link), then confirms.
    if (!confirm(
      `About to email the PRIVATE TEASER link to the client.\n\n` +
      `While test mode is on, the email goes to michael@kindraai.dev only.\n\n` +
      `Click OK to proceed. To preview the teaser page first, cancel and click "Preview teaser page".`
    )) return;
    setBusy(true); setErr('');
    try {
      const r = await axios.post(
        `${API}/admin/os/sessions/${sessionId}/send_teaser`,
        { confirm: true },
        auth(),
      );
      const modeLabel = r.data.test_mode ? '(test mode — internal preview)' : '';
      alert(`Teaser sent to ${r.data.emailed_to} ${modeLabel}\n\nLink: ${r.data.teaser_url}`.trim());
      load();
    } catch (e) {
      const d = e?.response?.data?.detail || '';
      if (d && d.startsWith && d.startsWith('teaser_scan_failed')) {
        // Never block anything — refresh the quarantine panel instead.
        setActionErr('');
        axios.get(`${API}/admin/os/sessions/${sessionId}/teaser_preview`, auth())
          .then((t) => setTeaser(t.data)).catch(() => {});
      } else {
        setActionErr(
          d === 'no_recipient_email' ? 'No client email on file. Add one or use the To override.'
          : d === 'report_not_generated' ? 'Generate the report first — no headline/leaks yet.'
          : d === 'resend_not_configured' ? 'Resend not configured on this environment.'
          : d || 'Could not send the teaser.'
        );
      }
    } finally { setBusy(false); }
  };

  // Mint (or fetch) the teaser token so the Preview link resolves to the
  // real client-facing page instead of a 404. Opens in a new tab.
  //
  // Chrome/Safari/Firefox block window.open() calls that happen AFTER an
  // async gap because they're no longer considered a "user-initiated
  // action". Michael's report ("preview doesn't work") was exactly this:
  // the axios.post finished, window.open ran, browser silently blocked
  // the popup. Fix: open a blank tab SYNCHRONOUSLY inside the click
  // handler, then set its href once we have the token.
  const openTeaserPreview = async () => {
    setBusy(true); setErr('');
    // Sync open — MUST happen before any await. Fallback to same-tab
    // navigation if the browser still blocks (rare but polite).
    const previewTab = window.open('about:blank', '_blank', 'noopener,noreferrer');
    try {
      const r = await axios.post(
        `${API}/admin/os/sessions/${sessionId}/mint_teaser_token`,
        {},
        auth(),
      );
      const url = r.data.teaser_url || `/leak/${r.data.teaser_token}`;
      if (previewTab && !previewTab.closed) {
        previewTab.location.href = url;
      } else {
        // Popup blocked — fall back to same-tab so Michael's click still
        // reaches the destination.
        window.location.href = url;
      }
    } catch (e) {
      if (previewTab && !previewTab.closed) previewTab.close();
      setActionErr(e?.response?.data?.detail || 'Could not open teaser preview.');
    } finally { setBusy(false); }
  };

  return (
    <Chrome activeTab="queue" crumb={<><Link to="/admin/os" style={{ color: C.muted, textDecoration: 'none' }}>Client reports</Link> <span style={{ margin: '0 8px' }}>/</span> {s.business_name || s.business_name_hint || (s.linked ? 'Booked client' : 'Anonymous')}</>}>
      {/* Title row */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <p style={{ fontFamily: 'Source Sans 3, sans-serif', color: C.faint, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            Discovery · {fmtDate(s.completed_at || s.created_at)}
          </p>
          <h1 style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy, fontSize: 'clamp(26px,3.4vw,34px)', lineHeight: 1.15, marginTop: 6 }}>
            {s.business_name || s.business_name_hint || (s.linked ? (s.contact_name ? `${s.contact_name}'s business` : 'Booked client (no business name yet)') : 'Anonymous session')}
          </h1>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <StatusPill
              status={s.status}
              hasReport={!!(s.report && (s.report.headline || (s.report.leak_report || []).length))}
              reportError={s.report_error}
            />
            {s.linked ? <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.muted }}>· Bound to booking {s.booking_id?.slice(0, 12)}…</span> : null}
            {s.contact_name && <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.muted }}>· {s.contact_name}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap kindra-action-row" data-testid="os-detail-actions">
          {/* Teaser quarantine — a dirty auto-preview NEVER blocks the
              session; it parks here with a regenerate/hand-edit path. */}
          {teaserDirty && (
            <div data-testid="teaser-quarantine" style={{ flexBasis: '100%', padding: '14px 16px',
                          borderRadius: 12, background: 'rgba(247,150,64,0.10)',
                          border: '1px solid rgba(247,150,64,0.5)', marginBottom: 4 }}>
              <p style={{ color: '#F79640', fontFamily: 'Source Sans 3, sans-serif', fontWeight: 700, fontSize: 13.5, margin: '0 0 6px' }}>
                ⚠ The auto-written client preview contained internal terms and was blocked before anyone saw it.
              </p>
              <ul style={{ color: C.muted, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, margin: '0 0 10px 18px' }}>
                {teaserHitsPlain(teaser.hits).map((h, i) => <li key={i} data-testid={`teaser-hit-${i}`}>{h}</li>)}
              </ul>
              {!tEdits && (
                <button onClick={regenTeaser} disabled={tBusy} data-testid="teaser-regen-btn"
                        style={{ padding: '9px 18px', borderRadius: 999, border: 'none',
                                  background: '#F79640', color: '#0E0F11', fontFamily: 'Source Sans 3, sans-serif',
                                  fontWeight: 700, fontSize: 12.5, cursor: tBusy ? 'wait' : 'pointer' }}>
                  {tBusy ? 'Cleaning…' : 'Regenerate clean preview'}
                </button>
              )}
              {tEdits && (
                <div data-testid="teaser-hand-edit">
                  <p style={{ color: C.muted, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, margin: '0 0 8px' }}>
                    Auto-clean couldn't get it spotless — edit the lines yourself, then rescan. Send stays off until the scan passes.
                  </p>
                  {tEdits.map((r, i) => (
                    <div key={r.leak_area} style={{ marginBottom: 8 }}>
                      <div style={{ color: C.icy, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{r.leak_area}</div>
                      {['title', 'pain', 'outcome'].map((f) => (
                        <textarea key={f} rows={f === 'title' ? 1 : 2} value={r[f]}
                          data-testid={`teaser-edit-${r.leak_area}-${f}`}
                          onChange={(e) => setTEdits(tEdits.map((x, j) => (j === i ? { ...x, [f]: e.target.value } : x)))}
                          style={{ width: '100%', marginBottom: 4, padding: '8px 10px', borderRadius: 8,
                                    background: C.navy, border: `1px solid ${C.hair}`, color: C.icy,
                                    fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, resize: 'vertical' }} />
                      ))}
                    </div>
                  ))}
                  <button onClick={rescanTeaser} disabled={tBusy} data-testid="teaser-rescan-btn"
                          style={{ padding: '9px 18px', borderRadius: 999, border: 'none',
                                    background: '#F79640', color: '#0E0F11', fontFamily: 'Source Sans 3, sans-serif',
                                    fontWeight: 700, fontSize: 12.5, cursor: tBusy ? 'wait' : 'pointer' }}>
                    {tBusy ? 'Rescanning…' : 'Rescan'}
                  </button>
                </div>
              )}
            </div>
          )}
          {actionErr && (
            <p data-testid="os-action-err" style={{ flexBasis: '100%', color: '#FFB469',
                          fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, margin: '0 0 4px' }}>{actionErr}</p>
          )}
          {/* Discovery reminder — only when the session is LINKED (booked
              client) AND the chat isn't done yet. Explicit Michael click
              fires the branded "first step" reminder via Resend. Never
              auto-sent. */}
          {!editing && s.linked && s.status !== 'reviewed' && s.status !== 'ready_for_review' && (
            <button onClick={sendDiscoveryReminder} data-testid="os-send-discovery-reminder" disabled={busy}
                    style={{ padding: '10px 18px', borderRadius: 999, border: `1px solid ${C.hairBright}`,
                              background: 'transparent', color: '#F79640', fontFamily: 'Source Sans 3, sans-serif', fontWeight: 600,
                              fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
              {busy ? 'Sending…' : 'Send discovery reminder →'}
            </button>
          )}
          {/* Quote actions — only visible once the session has been
              marked reviewed. Michael drives prices manually; we do not
              auto-price. */}
          {!editing && s.status === 'reviewed' && (data.quotes || []).length === 0 && (
            <button onClick={createQuote} data-testid="os-create-quote-btn" disabled={busy}
                    style={{ padding: '10px 20px', borderRadius: 999, border: 'none',
                              background: C.spectrum, color: '#fff', fontFamily: 'Source Sans 3, sans-serif', fontWeight: 700,
                              fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
              {busy ? 'Creating…' : 'Create quote →'}
            </button>
          )}
          {!editing && (data.quotes || []).length > 0 && (
            <Link to={`/admin/os/quotes/${data.quotes[0].quote_id}`} data-testid="os-open-quote-btn"
                  style={{ padding: '10px 18px', borderRadius: 999, border: `1px solid ${C.hairBright}`,
                            background: 'transparent', color: C.icy, fontFamily: 'Source Sans 3, sans-serif', fontWeight: 600,
                            fontSize: 13, cursor: 'pointer', textDecoration: 'none' }}>
              Open quote →
            </Link>
          )}
          {!editing && s.status !== 'reviewed' && s.report && (
            <button onClick={startEditing} data-testid="os-edit-btn" disabled={busy}
                    style={{ padding: '10px 18px', borderRadius: 999, border: `1px solid ${C.hairBright}`,
                              background: 'transparent', color: C.icy, fontFamily: 'Source Sans 3, sans-serif', fontWeight: 600,
                              fontSize: 13, cursor: 'pointer' }}>
              Edit report
            </button>
          )}
          {!editing && s.report && s.status !== 'reviewed' && (
            <button onClick={() => save({ markReviewed: true })} data-testid="os-mark-reviewed-btn" disabled={busy}
                    title="Marks this off your daily list. Doesn't send anything to the client."
                    style={{ padding: '10px 20px', borderRadius: 999, border: 'none',
                              background: C.spectrum, color: '#fff', fontFamily: 'Source Sans 3, sans-serif', fontWeight: 700,
                              fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
              {busy ? 'Saving…' : 'Mark reviewed →'}
            </button>
          )}
          {/* Send teaser to client — private /leak/{token} link, emailed
              via Resend. Test mode ships to michael@kindraai.dev only. */}
          {!editing && s.report && (
            <button onClick={sendClientReport} data-testid="os-send-client-report" disabled={busy || teaserDirty}
                    title={teaserDirty ? 'Blocked until the client preview passes the safety scan' : "Emails a free-preview link to the client — you'll see a confirmation modal first"}
                    style={{ padding: '10px 18px', borderRadius: 999, border: `1px solid rgba(84,209,151,0.55)`,
                              background: 'rgba(84,209,151,0.10)', color: '#7FE0C0', fontFamily: 'Source Sans 3, sans-serif', fontWeight: 600,
                              fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
              {busy ? 'Sending…' : '✉ Send free-preview email'}
            </button>
          )}
          {!editing && s.report && (
            <button onClick={openTeaserPreview}
                    data-testid="os-preview-client-pdf"
                    disabled={busy || teaserDirty}
                    title={teaserDirty ? 'Blocked until the client preview passes the safety scan' : 'See exactly what the client will see — nothing is sent'}
                    style={{ padding: '10px 14px', borderRadius: 999, border: `1px solid ${C.hair}`,
                              background: 'transparent', color: C.muted, fontFamily: 'Source Sans 3, sans-serif', fontWeight: 500,
                              fontSize: 12.5, cursor: busy ? 'wait' : 'pointer' }}>
              Preview what the client sees
            </button>
          )}
          {/* Always-visible Regenerate button — Michael can re-run the
              auto-report at any time, regardless of session status, in case
              the first run failed silently or the prompt has been tuned. */}
          {!editing && (
            <button onClick={regenerate} data-testid="os-regenerate-anytime" disabled={busy}
                    style={{ padding: '10px 18px', borderRadius: 999, border: `1px solid ${C.hair}`,
                              background: 'transparent', color: C.muted, fontFamily: 'Source Sans 3, sans-serif', fontWeight: 500,
                              fontSize: 12.5, cursor: 'pointer' }}>
              ↻ Regenerate report
            </button>
          )}
          {/* Download PDF — window.print() drives the light "study" theme
              via @media print. Only useful once the report exists. */}
          {!editing && s.report && (
            <PrintPdfButton
              sessionId={s.session_id}
              businessName={s.business_name || s.business_name_hint}
            />
          )}
          {editing && (
            <>
              <button onClick={cancelEditing} disabled={busy}
                      style={{ padding: '10px 18px', borderRadius: 999, border: `1px solid ${C.hair}`,
                                background: 'transparent', color: C.muted, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => save({})} data-testid="os-save-btn" disabled={busy}
                      style={{ padding: '10px 20px', borderRadius: 999, border: 'none',
                                background: C.spectrum, color: '#fff', fontFamily: 'Source Sans 3, sans-serif', fontWeight: 700,
                                fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
                {busy ? 'Saving…' : 'Save edits'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Auto-report failure banner — shown when the LLM pipeline
          fell back to an empty scaffold. Surfaces the actual error and
          a prominent Retry so Michael never has to wonder if the auto
          step failed silently. */}
      {s.report && (s.report._report_failed || s.report_error) && (
        <Card title="Auto-report failed" testId="os-report-failed">
          <p style={{ color: '#EC7C24', fontFamily: 'Source Sans 3, sans-serif', fontSize: 13.5, lineHeight: 1.6, marginBottom: 10, fontWeight: 600 }}>
            The auto-report background task fell back to an empty scaffold.
          </p>
          {(s.report._report_error || s.report_error) && (
            <pre style={{ background: 'rgba(236,124,36,0.06)', border: '1px solid rgba(236,124,36,0.35)',
                          borderRadius: 10, padding: '10px 12px', fontFamily: 'ui-monospace, monospace',
                          fontSize: 12, color: C.icy, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          marginBottom: 12, overflow: 'auto' }}>
              {s.report._report_error || s.report_error}
            </pre>
          )}
          <button onClick={regenerate} disabled={busy} data-testid="os-regenerate-failure"
                  style={{ padding: '10px 22px', borderRadius: 999, border: 'none',
                            background: C.spectrum, color: '#fff', fontFamily: 'Source Sans 3, sans-serif', fontWeight: 700,
                            fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
            {busy ? 'Re-running…' : '↻ Retry auto-report'}
          </button>
        </Card>
      )}

      {/* Headline / no-report fallback */}
      {!s.report ? (
        <Card title="Report not generated yet" testId="os-no-report">
          <p style={{ color: C.muted, fontFamily: 'Source Sans 3, sans-serif', fontSize: 14, lineHeight: 1.6, marginBottom: 14 }}>
            This session hasn&apos;t completed all 5 leak areas yet, or the auto-report background task hasn&apos;t finished.
            You can regenerate it manually once the discovery completes.
          </p>
          <button onClick={regenerate} disabled={busy} data-testid="os-regenerate"
                  style={{ padding: '10px 18px', borderRadius: 999, border: `1px solid ${C.hairBright}`,
                            background: 'transparent', color: C.icy, fontFamily: 'Source Sans 3, sans-serif', fontWeight: 600,
                            fontSize: 13, cursor: 'pointer' }}>
            Regenerate report
          </button>
        </Card>
      ) : (
        <>
          {/* Proposals — quote list with status pills + totals. Surfaces
              the Quote & Win pipeline directly inside the review screen. */}
          {(data.quotes || []).length > 0 && (
            <Card title="Proposals" testId="os-quotes-card">
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {(data.quotes || []).map((q) => (
                  <li key={q.quote_id} className="flex items-center justify-between gap-3 flex-wrap"
                      style={{ padding: '10px 0', borderBottom: `1px solid ${C.hair}` }}>
                    <Link to={`/admin/os/quotes/${q.quote_id}`} data-testid={`os-quote-row-${q.quote_id}`}
                          style={{ fontFamily: 'Source Sans 3, sans-serif', color: C.icy, textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>
                      {q.title || 'Untitled proposal'}
                    </Link>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: C.faint, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{q.status}</span>
                      <span style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy, fontSize: 14 }}>
                        {new Intl.NumberFormat('en-AU', { style: 'currency', currency: q.currency || 'AUD', maximumFractionDigits: 0 }).format(q.total || 0)}
                      </span>
                      {q.token && (
                        <a href={`/quote/${q.token}`} target="_blank" rel="noreferrer"
                          style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: C.muted, textDecoration: 'underline' }}>
                          client view ↗
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {/* Headline (edit-only fallback — read-mode is rendered by
              V2GroundedReport below with 11 grounded sections). */}
          {!editing && (
            <V2GroundedReport session={s} report={report} />
          )}
          {editing && (<>
          <Card title="Headline" testId="os-headline-card">
            {!editing ? (
              <p data-testid="os-headline" style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy,
                  fontSize: 22, lineHeight: 1.3, letterSpacing: '-0.01em' }}>
                {report.headline || '—'}
              </p>
            ) : (
              <textarea
                value={report.headline || ''}
                onChange={(e) => updateField(['headline'], e.target.value)}
                rows={2}
                style={{ width: '100%', background: 'rgba(241,242,243,0.04)', border: `1px solid ${C.hair}`,
                          color: C.icy, padding: '10px 12px', borderRadius: 10, fontFamily: 'Fredoka, sans-serif',
                          fontSize: 18, lineHeight: 1.35, outline: 'none' }}
              />
            )}
          </Card>

          {/* Leak report */}
          <Card title="Leak report · advice + how-to" testId="os-leak-report-card">
            <div className="grid gap-3">
              {LEAK_ORDER.map((area) => {
                const i = (report.leak_report || []).findIndex((l) => l.leak_area === area);
                const l = i >= 0 ? report.leak_report[i] : {
                  leak_area: area, severity: 'none', confidence: 'low', summary: '', evidence: [], estimated_impact: '',
                  recommendation: { what_to_do: '', how_to_do_it: '', effort: 'medium_build', impact: '', own_it_note: '' },
                };
                const rec = l.recommendation || {};
                const effortMap = {
                  quick_win:      { label: 'Quick win',      color: '#7FE0C0', bg: 'rgba(84,209,151,0.12)', border: 'rgba(84,209,151,0.40)' },
                  medium_build:   { label: 'Medium build',   color: '#9EC1E8', bg: 'rgba(123,176,236,0.14)', border: 'rgba(123,176,236,0.40)' },
                  bigger_project: { label: 'Bigger project', color: '#FFB469', bg: 'rgba(200,90,18,0.14)', border: 'rgba(200,90,18,0.40)' },
                };
                const ef = effortMap[rec.effort] || effortMap.medium_build;
                return (
                  <div key={area} className="rounded-xl"
                       data-testid={`os-leak-${area}`}
                       style={{ background: 'rgba(241,242,243,0.03)', border: `1px solid ${C.hair}`, padding: '16px 18px' }}>
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                      <div style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy, fontSize: 16 }}>
                        <LeakDot area={area} />{LEAK_LABEL[area]}
                      </div>
                      <div className="flex items-center gap-2">
                        <SeverityPill sev={l.severity} />
                        {l.confidence && (
                          <span title="Our confidence in this signal"
                                style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: C.faint, padding: '3px 8px',
                                          borderRadius: 999, border: `1px solid ${C.hair}` }}>
                            {l.confidence} confidence
                          </span>
                        )}
                      </div>
                    </div>
                    {!editing ? (
                      <>
                        {l.summary && (
                          <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 14, color: C.muted, lineHeight: 1.6 }}>{l.summary}</p>
                        )}
                        {l.evidence && l.evidence.length > 0 && (
                          <ul style={{ margin: '10px 0 0 0', padding: 0, listStyle: 'none' }}>
                            {l.evidence.map((q, j) => (
                              <li key={j} style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.faint, fontStyle: 'italic', lineHeight: 1.55, padding: '3px 0' }}>“{q}”</li>
                            ))}
                          </ul>
                        )}
                        {l.estimated_impact && (
                          <div className="mt-3" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: C.faint }}>
                            <span style={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600 }}>Estimated impact · </span>
                            <span style={{ color: C.icy }}>{l.estimated_impact}</span>
                          </div>
                        )}
                        {/* Recommendation block — the new advice + how-to layer */}
                        {(rec.what_to_do || rec.how_to_do_it || rec.impact) && (
                          <div className="mt-4 rounded-lg" style={{ background: 'rgba(241,242,243,0.04)', border: `1px solid ${C.hair}`, padding: '14px 16px' }}>
                            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                              <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.faint, fontWeight: 700 }}>
                                Recommendation · what to build
                              </div>
                              <span data-testid={`os-effort-${area}`}
                                    style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
                                              background: ef.bg, color: ef.color, border: `1px solid ${ef.border}`,
                                              letterSpacing: '0.04em' }}>
                                {ef.label}
                              </span>
                            </div>
                            {rec.what_to_do && (
                              <div style={{ marginBottom: 10 }}>
                                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint, marginBottom: 4 }}>What to do</div>
                                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 14, color: C.icy, lineHeight: 1.55 }}>{rec.what_to_do}</div>
                              </div>
                            )}
                            {rec.how_to_do_it && (
                              <div style={{ marginBottom: 10 }}>
                                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint, marginBottom: 4 }}>How to do it</div>
                                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 14, color: C.muted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{rec.how_to_do_it}</div>
                              </div>
                            )}
                            {rec.impact && (
                              <div>
                                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint, marginBottom: 4 }}>Why it matters</div>
                                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.muted, lineHeight: 1.55 }}>{rec.impact}</div>
                              </div>
                            )}
                            {rec.own_it_note && (
                              <div data-testid={`os-own-it-${area}`} className="mt-3 rounded-md"
                                   style={{ background: 'rgba(84,209,151,0.06)', border: '1px dashed rgba(84,209,151,0.35)', padding: '10px 12px' }}>
                                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(84,209,151,0.85)', marginBottom: 4, fontWeight: 700 }}>
                                  Internal · build-path framing
                                </div>
                                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.icy, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{rec.own_it_note}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="grid gap-2">
                        <textarea value={l.summary || ''} rows={2}
                          onChange={(e) => updateField(['leak_report', i >= 0 ? i : 0, 'summary'], e.target.value)}
                          placeholder="Summary — what's leaking, in plain English"
                          style={{ width: '100%', background: 'rgba(241,242,243,0.04)', border: `1px solid ${C.hair}`, color: C.icy, padding: '8px 10px', borderRadius: 8, fontFamily: 'Source Sans 3, sans-serif', fontSize: 14 }}
                        />
                        <input value={l.estimated_impact || ''}
                          onChange={(e) => updateField(['leak_report', i >= 0 ? i : 0, 'estimated_impact'], e.target.value)}
                          placeholder="Estimated impact (hrs/wk · $/mo · jobs lost)"
                          style={{ width: '100%', background: 'rgba(241,242,243,0.04)', border: `1px solid ${C.hair}`, color: C.icy, padding: '8px 10px', borderRadius: 8, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13 }}
                        />
                        <input value={rec.what_to_do || ''}
                          onChange={(e) => updateField(['leak_report', i >= 0 ? i : 0, 'recommendation', 'what_to_do'], e.target.value)}
                          placeholder="What to do — the recommended fix"
                          style={{ width: '100%', background: 'rgba(241,242,243,0.04)', border: `1px solid ${C.hair}`, color: C.icy, padding: '8px 10px', borderRadius: 8, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13 }}
                        />
                        <textarea value={rec.how_to_do_it || ''} rows={3}
                          onChange={(e) => updateField(['leak_report', i >= 0 ? i : 0, 'recommendation', 'how_to_do_it'], e.target.value)}
                          placeholder="How to do it — the practical approach you'd build"
                          style={{ width: '100%', background: 'rgba(241,242,243,0.04)', border: `1px solid ${C.hair}`, color: C.icy, padding: '8px 10px', borderRadius: 8, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13 }}
                        />
                        <div className="flex items-center gap-2 flex-wrap">
                          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: C.faint }}>Effort:</span>
                          {['quick_win', 'medium_build', 'bigger_project'].map((k) => (
                            <button key={k} type="button"
                              onClick={() => updateField(['leak_report', i >= 0 ? i : 0, 'recommendation', 'effort'], k)}
                              style={{ padding: '4px 10px', borderRadius: 999, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12,
                                background: rec.effort === k ? 'rgba(241,242,243,0.10)' : 'transparent',
                                color: rec.effort === k ? C.icy : C.muted,
                                border: `1px solid ${rec.effort === k ? C.hairBright : C.hair}`, cursor: 'pointer' }}>
                              {k.replace('_', ' ')}
                            </button>
                          ))}
                        </div>
                        <input value={rec.impact || ''}
                          onChange={(e) => updateField(['leak_report', i >= 0 ? i : 0, 'recommendation', 'impact'], e.target.value)}
                          placeholder="Why it matters for this client"
                          style={{ width: '100%', background: 'rgba(241,242,243,0.04)', border: `1px solid ${C.hair}`, color: C.icy, padding: '8px 10px', borderRadius: 8, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13 }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Plan of attack — Michael's 20-second pre-call brief */}
          {(((report.plan_of_attack || {}).sequence?.length || 0) > 0 ||
             ((report.plan_of_attack || {}).how_id_approach_this || '').length > 0) && (
            <Card title="Plan of attack · 20-second pre-call brief" testId="os-plan-of-attack-card"
                  right={<span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.faint }}>Sequenced · value-first</span>}>
              {!editing ? (
                <>
                  {(report.plan_of_attack?.how_id_approach_this || '') && (
                    <div className="rounded-xl mb-4" style={{
                      background: 'rgba(241,242,243,0.04)',
                      border: `1px solid ${C.hair}`,
                      borderLeft: '3px solid #EC7C24',
                      padding: '14px 18px',
                    }}>
                      <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#FFB469', fontWeight: 700, marginBottom: 6 }}>
                        How I&apos;d approach this build
                      </div>
                      <p data-testid="os-plan-approach" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 15, color: C.icy, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {report.plan_of_attack.how_id_approach_this}
                      </p>
                    </div>
                  )}
                  {(report.plan_of_attack?.sequence || []).length > 0 && (
                    <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {report.plan_of_attack.sequence.map((step, i) => (
                        <li key={i} data-testid={`os-plan-step-${step.priority}`} className="rounded-lg mb-2"
                            style={{ background: 'rgba(241,242,243,0.03)', border: `1px solid ${C.hair}`, padding: '12px 16px' }}>
                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <span style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 700, color: C.icy, fontSize: 13,
                                            width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            borderRadius: '50%', background: LEAK_COLOR[step.leak_area] || 'rgba(241,242,243,0.10)' }}>
                              {step.priority}
                            </span>
                            <span style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy, fontSize: 15 }}>
                              <LeakDot area={step.leak_area} />{LEAK_LABEL[step.leak_area]}
                            </span>
                          </div>
                          <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.muted, lineHeight: 1.55, paddingLeft: 36 }}>
                            {step.why_this_order}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </>
              ) : (
                <textarea
                  rows={5}
                  value={(report.plan_of_attack || {}).how_id_approach_this || ''}
                  onChange={(e) => updateField(['plan_of_attack', 'how_id_approach_this'], e.target.value)}
                  placeholder="20-second pre-call brief — where to start, what to bundle, what to leave for later"
                  style={{ width: '100%', background: 'rgba(241,242,243,0.04)', border: `1px solid ${C.hair}`, color: C.icy, padding: '10px 12px', borderRadius: 10, fontFamily: 'Source Sans 3, sans-serif', fontSize: 14, lineHeight: 1.55, outline: 'none', resize: 'vertical' }}
                />
              )}
            </Card>
          )}

          {/* Business map */}
          <Card title="Business map" testId="os-business-map-card">
            <BusinessMap stages={(report.business_map || {}).stages} />
          </Card>

          {/* Recommendations */}
          <Card title="Recommendations · action plan" testId="os-recs-card">
            {(report.recommendations || []).length === 0 && (
              <div style={{ color: C.faint, fontFamily: 'Source Sans 3, sans-serif' }}>No recommendations.</div>
            )}
            <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {(report.recommendations || []).map((r, i) => (
                <li key={i} data-testid={`os-rec-${i+1}`} className="rounded-xl mb-2"
                    style={{ background: 'rgba(241,242,243,0.03)', border: `1px solid ${C.hair}`, padding: '14px 16px' }}>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy, fontSize: 14,
                                    width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    borderRadius: '50%', background: 'rgba(241,242,243,0.06)' }}>{r.priority}</span>
                    <span style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy, fontSize: 15 }}>{r.what}</span>
                    <div className="flex gap-1">
                      {(r.linked_leaks || []).map((la) => (
                        <span key={la} title={LEAK_LABEL[la]}
                              style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: LEAK_COLOR[la] }} />
                      ))}
                    </div>
                  </div>
                  <div className="mt-2 grid md:grid-cols-2 gap-3">
                    <div><div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint, marginBottom: 4 }}>Why</div>
                         <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{r.why}</div></div>
                    <div><div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint, marginBottom: 4 }}>Expected impact</div>
                         <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{r.expected_impact}</div></div>
                  </div>
                </li>
              ))}
            </ol>
          </Card>

          {/* Talking points */}
          {(report.talking_points_for_call || []).length > 0 && (
            <Card title="Talking points for the call" testId="os-talking-points-card">
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {report.talking_points_for_call.map((tp, i) => (
                  <li key={i} style={{ padding: '8px 0', borderBottom: i < report.talking_points_for_call.length - 1 ? `1px solid ${C.hair}` : 'none',
                       fontFamily: 'Source Sans 3, sans-serif', fontSize: 14, color: C.icy, lineHeight: 1.55 }}>
                    <span style={{ marginRight: 10, color: '#EC7C24' }}>›</span>{tp}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          </>)}
        </>
      )}

      {/* Coverage + confidence side panel — only visible in edit mode; the
          read-mode grounded report already contains §09 Coverage & Confidence. */}
      {editing && (
      <Card title="Coverage & confidence" testId="os-coverage-card">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {LEAK_ORDER.map((a) => {
            const covered = (s.coverage || {})[a];
            const conf = (s.confidence || {})[a] || 'low';
            return (
              <div key={a} className="rounded-lg" style={{ background: 'rgba(241,242,243,0.04)', border: `1px solid ${C.hair}`, padding: '10px 12px' }}>
                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint }}>
                  <LeakDot area={a} />{LEAK_LABEL[a]}
                </div>
                <div style={{ marginTop: 6, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: covered ? C.icy : C.faint }}>
                  {covered ? `✓ ${conf}` : '—'}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      )}

      <TranscriptCard transcript={s.transcript || []} businessName={s.business_name || s.business_name_hint} />

      <Card title="Internal review notes" testId="os-notes-card">
        <textarea
          data-testid="os-notes-input"
          value={reviewNotes}
          onChange={(e) => setReviewNotes(e.target.value)}
          placeholder="Anything to remember for the call — not shown to the client."
          rows={3}
          style={{ width: '100%', background: 'rgba(241,242,243,0.04)', border: `1px solid ${C.hair}`,
                    color: C.icy, padding: '10px 12px', borderRadius: 10, fontFamily: 'Source Sans 3, sans-serif',
                    fontSize: 14, lineHeight: 1.55, outline: 'none', resize: 'vertical' }}
        />
        {reviewNotes !== (s.review_notes || '') && (
          <div className="mt-3">
            <button onClick={() => save({})} disabled={busy} data-testid="os-save-notes"
                    style={{ padding: '8px 16px', borderRadius: 999, border: `1px solid ${C.hairBright}`,
                              background: 'transparent', color: C.icy, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, cursor: 'pointer' }}>
              Save notes
            </button>
          </div>
        )}
      </Card>

      {/* Transcript (collapsible) */}
      <Card title="Discovery transcript" testId="os-transcript-card"
            right={
              <button onClick={() => setShowTranscript((v) => !v)}
                      style={{ background: 'transparent', border: 'none', color: C.muted, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, cursor: 'pointer' }}>
                {showTranscript ? 'Hide' : 'Show'} · {(s.transcript || []).length} turns
              </button>
            }>
        {showTranscript && (
          <div className="space-y-2" style={{ maxHeight: 480, overflowY: 'auto' }}>
            {(s.transcript || []).map((t, i) => (
              <div key={i} className="flex gap-3">
                <div style={{ minWidth: 70, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.14em', paddingTop: 2 }}>
                  {t.role === 'user' ? 'Owner' : 'Kindra'}
                </div>
                <div style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 14, color: t.role === 'user' ? C.icy : C.muted, lineHeight: 1.55 }}>
                  {t.content}
                </div>
              </div>
            ))}
          </div>
        )}
        {!showTranscript && (
          <div style={{ color: C.faint, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13 }}>Transcript hidden by default — toggle to view.</div>
        )}
      </Card>
    </Chrome>
  );
};

export default V2AdminOSQueuePage;

// Re-export shared cockpit primitives so the sibling Delivery pages can
// match the OS visual system without duplicating constants.
export { Chrome as V2OSChrome, C as V2OSC, tokenStore as V2OSTokenStore, auth as v2OSAuth, LEAK_COLOR as V2OSLeakColor };
